import moment from 'moment';
import {
  Address,
  ApiData,
  CoverType,
  ReviewDetailsData,
  YesNoAnswer,
  PaymentFrequency,
  PolicyIssued,
} from 'scenes/common/types';
import {
  CoverOptionsData,
  HomeQuoteData,
  HomeQuoteDetails,
  KickOutChoice,
  OptionalExtra,
  PropertyPurposeType,
  SpecifiedContent,
  SpecifiedContentCover,
} from 'scenes/home/types';

import {
  WireAsset,
  WireCoverageOptionType,
  WireCoverType,
  WireItemType,
  WireLimit,
  WirePartyType,
  WirePolicyRole,
  WirePurpose,
} from 'scenes/common/api/quote/types';
import {
  toFlybuysNumber,
  toHasFlybuys,
} from 'scenes/common/api/quote/marshaller';
import { getApiData } from 'scenes/common/helpers/apiData';
import { toDDMMYYYY } from 'scenes/common/helpers/datetime';
import { toMainAddress } from 'scenes/common/api/quote/marshaller/address';
import { WireCampaignDetailsResponse } from 'scenes/common/api/campaign/types';
import { defaultSpecifiedContent } from 'scenes/home/flows/coverOptions/defaults';
import { wireCoverageId } from 'scenes/common/api/quote/marshaller/coverage';

import {
  toOwnedProperty,
  toUsedForBusiness,
  toBusinessType,
  toBuildingInformation,
  toAdditionalSecurityInstalled,
  toNumberOfBedrooms,
  toPropertyBuiltYear,
  toRoofMaterial,
  toWallMaterial,
  toSecurityFeatures,
  toSumInsuredType,
  booleanToYesNoUnspecified,
  toAgreedBuildingSumInsured,
  toEstimatedSumInsured,
} from 'scenes/common/api/quote/marshaller/retrieve';

import { certificateOfOccupancyAnswerRequired } from 'scenes/home/selectors';

import {
  WireCoverageName,
  WireHomeCoverageResponse,
  WireHomeParty,
  WireHomePolicyResponse,
  WireHomeQuotePayload,
} from '../types';

import { toOptionalExtra } from './option';
import {
  findSelectedExtras,
  hasGeneralPortables,
  hasSpecifiedPortables,
} from './premium';

function toCoverType(payload: WireHomeQuotePayload): CoverType {
  switch (payload.coverType) {
    case WireCoverType.BuildingAndContents:
      return CoverType.BuildingAndContents;
    case WireCoverType.Building:
      return CoverType.Building;
    case WireCoverType.Contents:
      return CoverType.Contents;
    default:
      return CoverType.Unspecified;
  }
}

const toPolicyStartDate = (policy: WireHomePolicyResponse): string => {
  if (policy.dates) return policy.dates.effective;
  return moment().format('YYYY-MM-DD');
};

const toRiskAddress = (payload: WireHomeQuotePayload): Address => {
  const { addresses } = payload;
  return toMainAddress(addresses);
};

const toPropertyPurpose = (assets: WireAsset[]): PropertyPurposeType => {
  const asset = assets[0];

  switch (asset?.purpose) {
    case WirePurpose.OwnNLive:
      return PropertyPurposeType.PPR;
    case WirePurpose.HldHmIncm:
      return PropertyPurposeType.HolidaysHouse;
    case WirePurpose.OwnNRented:
      return PropertyPurposeType.Investment;

    default:
      return PropertyPurposeType.Unspecified;
  }
};

const toYearsInsured = (parties: WireHomeParty[]): number => {
  if (parties[0]?.numberOfYearsInsured === undefined) {
    return 0;
  }

  return parseInt(parties[0].numberOfYearsInsured, 10);
};

const toClaimsMade = (parties: WireHomeParty[]): number => {
  if (parties[0]?.claims?.last3Years === undefined) {
    return 0;
  }

  return parseInt(parties[0].claims.last3Years, 10);
};

const toContentsSumInsured = (coverages: WireHomeCoverageResponse[]) => {
  const contentInsured = coverages[0]?.sumInsureds?.find(
    (insured) => insured.type === WireCoverageName.Contents
  );
  const amount = contentInsured?.amount;

  return amount ? amount.toString() : '';
};

// If claimsMade is zero, set broken in claims to be Unspecified
const toBrokenInClaims = (
  policy: WireHomePolicyResponse,
  claimsMade: number
): YesNoAnswer =>
  claimsMade
    ? booleanToYesNoUnspecified(policy.history?.hasBreakInClaims)
    : YesNoAnswer.Unspecified;

export const toValidPolicyStartDate = (quote: WireHomeQuotePayload): string => {
  if (quote.policy.dates) {
    return moment(quote.policy.dates.effective).isSameOrAfter(moment())
      ? quote.policy.dates.effective
      : moment().format('YYYY-MM-DD');
  }
  return moment().format('YYYY-MM-DD');
};

export const toPromoCode = (campaigns?: WireCampaignDetailsResponse[]) =>
  campaigns?.[0]?.promoCode ?? '';

// Retrieve API returns hasCOO as false even if unanswered during quote creation.
// Therefore, return unspecified if no COC answer was required during quote
// creation.
const retrievePrimaryPolicyHolder = (
  parties: WireHomeParty[]
): WireHomeParty | undefined =>
  parties.find((party) => party.role === WirePolicyRole.PrimaryPolicyHolder);

const toDOB = (parties: WireHomeParty[]): string => {
  const party = retrievePrimaryPolicyHolder(parties);

  return toDDMMYYYY(party?.dob);
};

export const toPolicyHolder = (parties: WireHomeParty[]): PolicyIssued => {
  const party = parties[0];
  if (party?.type === WirePartyType.Company) {
    return PolicyIssued.Business;
  }

  return PolicyIssued.Person;
};

export const toCertificateOfOccupancy = (assets: WireAsset[]): YesNoAnswer => {
  if (certificateOfOccupancyAnswerRequired(toPropertyBuiltYear(assets))) {
    return booleanToYesNoUnspecified(assets[0]?.building?.hasCOO);
  }
  return YesNoAnswer.Unspecified;
};

export const toQuoteDetails = (
  quoteDetails: ApiData<HomeQuoteDetails>
): HomeQuoteData => {
  const { payload } = getApiData(quoteDetails);
  const { flybuys, policy, assets, parties, campaigns, coverages } = payload;

  const claimsMade = toClaimsMade(parties);
  const brokenInClaims = toBrokenInClaims(policy, claimsMade);
  const additionalSecurityInstalled = toAdditionalSecurityInstalled(
    assets,
    brokenInClaims
  );

  const ret = {
    coverType: toCoverType(payload),
    prefilledCampaignId: '',
    prefilledPromoCode: '',
    colesCampaignCode: '',
    policyStartDate: toPolicyStartDate(policy),
    riskAddress: toRiskAddress(payload),
    ownedProperty: toOwnedProperty(assets),

    propertyUsedForBusiness: toUsedForBusiness(assets),
    propertyBusinessType: toBusinessType(assets),
    purposeOfProperty: toPropertyPurpose(assets),

    kickOutChoice: KickOutChoice.Unspecified,

    policyIssued: toPolicyHolder(payload.parties),
    policyHolderDateOfBirth:
      toPolicyHolder(payload.parties) === PolicyIssued.Person
        ? toDOB(payload.parties)
        : '',
    businessOwnerDateOfBirth:
      toPolicyHolder(payload.parties) === PolicyIssued.Business
        ? toDOB(payload.parties)
        : '',

    recentInsurer: policy?.previousInsurer || '',
    yearsInsured: toYearsInsured(parties),
    claimsMade,

    ...toBuildingInformation(assets),

    brokenInClaims,
    additionalSecurityInstalled,
    numberOfBedrooms: toNumberOfBedrooms(assets),
    propertyBuiltYear: toPropertyBuiltYear(assets),
    certificateOfOccupancy: toCertificateOfOccupancy(assets),

    roofMaterial: toRoofMaterial(assets),
    wallMaterial: toWallMaterial(assets),

    securityFeatures: toSecurityFeatures(assets),
    sumInsuredType: toSumInsuredType(assets),
    estimatedSumInsured: toEstimatedSumInsured(assets),

    agreedSumInsuredRange: {
      lowerLimit: '',
      upperLimit: '',
    },

    agreedBuildingSumInsured: toAgreedBuildingSumInsured(
      assets,
      coverages[0].sumInsureds
    ),
    contentsSumInsured: toContentsSumInsured(coverages),

    hasFlybuys: toHasFlybuys(flybuys),
    flybuysNumber: toFlybuysNumber(flybuys),

    promoCode: toPromoCode(campaigns),
  };

  return ret;
};

export const toSelectedExtras = (
  quoteDetails: ApiData<HomeQuoteDetails>
): OptionalExtra[] => {
  const { payload } = getApiData(quoteDetails);
  return findSelectedExtras(
    payload.premiums,
    wireCoverageId[payload.coverType]
  ).map((extra) => toOptionalExtra(extra.name as WireCoverageOptionType));
};

const toSpecifiedContents = (
  limits: Array<WireLimit>
): Array<SpecifiedContent> => {
  const specifiedContents = limits.find(
    (limit) => limit.name === WireItemType.SpecifiedPortables
  );
  return (
    specifiedContents?.items.map((item, index) => ({
      id: index,
      category: item.category || '',
      itemValue: item.value || '',
      description: item.description || '',
      cover: item.isOutOfHome
        ? SpecifiedContentCover.PortableContents
        : SpecifiedContentCover.Home,
    })) || []
  );
};

const toUnspecifiedPortablesContentLimit = (limits: Array<WireLimit>) =>
  limits
    .find(
      (limit: { name: WireItemType }) =>
        limit.name === WireItemType.GeneralPortables
    )
    ?.items.find((item) => item.selected)?.name || '';

export const toCoverOptions = (
  quoteDetails: ApiData<HomeQuoteDetails>
): CoverOptionsData => {
  const {
    payload: { limits, premiums, coverType },
  } = getApiData(quoteDetails);
  const specifiedContents = toSpecifiedContents(limits);
  const unspecifiedPortableContents = hasGeneralPortables(
    premiums,
    wireCoverageId[coverType]
  )
    ? YesNoAnswer.Yes
    : YesNoAnswer.No;

  return {
    selectedExtras: toSelectedExtras(quoteDetails),
    preselectedPaymentFrequency: PaymentFrequency.Annually,
    specifiedContent: defaultSpecifiedContent,
    specifiedContentsAnswer: hasSpecifiedPortables(
      premiums,
      wireCoverageId[coverType]
    )
      ? YesNoAnswer.Yes
      : YesNoAnswer.No,
    specifiedContents,
    unspecifiedPortableContents,
    unspecifiedPortableContentsLimit: toUnspecifiedPortablesContentLimit(
      limits
    ),
    isPortableContentsExtraSelected:
      specifiedContents.length > 0 ||
      unspecifiedPortableContents === YesNoAnswer.Yes,
  };
};

export const toReviewDetails = (
  quoteDetails: ApiData<HomeQuoteDetails>
): ReviewDetailsData => {
  const { payload } = getApiData(quoteDetails);

  return {
    policyStartDate: toValidPolicyStartDate(payload),
  };
};
